import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  Loader2,
  ScrollText,
} from "lucide-react";

import {
  GraphAttachmentPdfDialog,
  isPreviewableGraphAttachmentFilename,
  type GraphAttachmentRef,
} from "@/components/common/GraphAttachmentPdfDialog";

import { SensitiveText } from "@/components/common/Sensitive";
import { Button } from "@/components/ui/button";
import { useRightRail } from "@/context/RightRailContext";
import { ApiError, api } from "@/services/api";
import { cn } from "@/lib/utils";
import { formatDateTimePlain } from "@/lib/weekDates";
import type { BookingBlDocument, BookingDocument } from "@/types/api";
import { graphMailCategoryLabel } from "@/features/feed/feedVisuals";
import { CollapsibleRailSection } from "@/shell/CollapsibleRailSection";
import {
  railSectionStackClassName,
  railStackInlineScrollBodyClassName,
  railStackInlineScrollRootClassName,
  railStackSectionContentClassName,
  useRailStackPanel,
} from "@/shell/RailStackContext";
import {
  blDocumentTypeBadge,
  resolvedBlDocumentType,
  useBookingBlDocuments,
} from "./bookingBlDocuments";
import {
  deduplicateBookingDocumentsByFilename,
  findLinkedBlDocument,
  formatDocumentBytes,
  groupBookingDocumentsByReceived,
  isUsedForBookingUpdates,
  type BookingDocumentGroup,
} from "./bookingDocuments";

export function useBookingDocuments(sourceId: string | null) {
  const [documents, setDocuments] = useState<BookingDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedSourceId, setLoadedSourceId] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!sourceId) {
      setDocuments([]);
      setLoadedSourceId(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getBookingDocuments(sourceId)
      .then((rows) => {
        if (cancelled) return;
        setDocuments(rows);
        setLoadedSourceId(sourceId);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("Failed to load booking documents", err);
        setDocuments([]);
        setLoadedSourceId(sourceId);
        setError(
          err instanceof ApiError
            ? err.message
            : "Could not load documents. Please try again.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  useEffect(() => reload(), [reload]);

  return { documents, loading, error, retry: reload, loadedSourceId };
}

interface BookingDocumentsCardProps {
  documents: BookingDocument[];
  blDocuments: BookingBlDocument[];
  selectedBlId?: string | null;
  onOpenBl?: (id: string) => void;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  stackGrow?: boolean;
}

function fileIcon(filename: string) {
  const ext = filename.split(".").pop()?.toLowerCase();
  return ext === "xlsx" || ext === "xls" ? FileSpreadsheet : FileText;
}

function DownloadButton({ document }: { document: BookingDocument }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (!document.downloadable) return;
    setDownloading(true);
    setError(null);
    try {
      await api.downloadGraphAttachment(
        document.mailbox_id,
        document.message_id,
        document.attachment_id,
        document.filename,
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to download attachment";
      console.warn("Failed to download booking document:", err);
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      title={
        error ??
        (document.downloadable
          ? `Download ${document.filename}`
          : "This file is no longer available")
      }
      onClick={(e) => {
        e.stopPropagation();
        void handleClick();
      }}
      disabled={downloading || !document.downloadable}
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/10",
        (downloading || !document.downloadable) && "opacity-50",
        error && "text-destructive hover:bg-destructive/10",
      )}
    >
      {downloading ? (
        <Loader2 className="size-3.5 animate-spin" />
      ) : (
        <Download className="size-3.5" />
      )}
    </button>
  );
}

function DocumentFileRow({
  document,
  linkedBl,
  selected,
  onOpenBl,
}: {
  document: BookingDocument;
  linkedBl?: BookingBlDocument;
  selected?: boolean;
  onOpenBl?: (id: string) => void;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewable =
    document.downloadable &&
    isPreviewableGraphAttachmentFilename(document.filename);
  const attachment = useMemo<GraphAttachmentRef>(
    () => ({
      mailboxId: document.mailbox_id,
      messageId: document.message_id,
      attachmentId: document.attachment_id,
      filename: document.filename,
    }),
    [
      document.attachment_id,
      document.filename,
      document.mailbox_id,
      document.message_id,
    ],
  );

  const Icon = fileIcon(document.filename);
  const isInvoice = document.document_kind === "invoice";
  const isPackingList = document.document_kind === "packing_list";
  const usedForBookingUpdates = isUsedForBookingUpdates(document);
  const blType = linkedBl ? resolvedBlDocumentType(linkedBl) : null;
  const canOpenBl = linkedBl != null && onOpenBl != null;
  const blTitle = linkedBl
    ? `Open ${blDocumentTypeBadge(blType ?? "bl")} details`
    : undefined;
  const metaParts = [formatDocumentBytes(document.size_bytes)];
  if (linkedBl?.bl_number) {
    metaParts.push(linkedBl.bl_number);
  } else if (
    (isInvoice || isPackingList || usedForBookingUpdates) &&
    document.booking_ref
  ) {
    metaParts.push(document.booking_ref);
  }
  if (isInvoice) metaParts.push("Invoice");
  else if (isPackingList) metaParts.push("Packing list");

  return (
    <>
    <div
      className={cn(
        "flex items-center gap-2 rounded-md px-1 py-1",
        selected && "bg-accent/50",
      )}
    >
      <Icon className="size-3.5 shrink-0 text-primary/80" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {canOpenBl ? (
            <button
              type="button"
              title={blTitle}
              onClick={() => onOpenBl(linkedBl.id)}
              className="min-w-0 truncate text-left text-xs font-medium hover:underline"
            >
              {document.filename}
            </button>
          ) : (
            <p className="truncate text-xs font-medium" title={document.filename}>
              {document.filename}
            </p>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground">
          {metaParts.join(" · ")}
        </p>
      </div>
      {canOpenBl && (
        <button
          type="button"
          title={blTitle}
          aria-label={blTitle}
          aria-pressed={selected}
          onClick={() => onOpenBl(linkedBl.id)}
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/70 hover:text-foreground",
            selected && "bg-accent text-foreground",
          )}
        >
          <ScrollText className="size-3.5" />
        </button>
      )}
      {previewable && (
        <button
          type="button"
          title={`Preview ${document.filename}`}
          onClick={(e) => {
            e.stopPropagation();
            setPreviewOpen(true);
          }}
          className="flex size-7 shrink-0 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/10"
        >
          <Eye className="size-3.5" />
        </button>
      )}
      <DownloadButton document={document} />
    </div>

    <GraphAttachmentPdfDialog
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      attachment={previewOpen ? attachment : null}
      previewDescription="Booking document preview"
    />
    </>
  );
}

function DocumentGroupCard({
  group,
  blDocuments,
  selectedBlId,
  onOpenBl,
}: {
  group: BookingDocumentGroup;
  blDocuments: BookingBlDocument[];
  selectedBlId?: string | null;
  onOpenBl?: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const received = formatDateTimePlain(group.received_at);
  const category = group.category
    ? graphMailCategoryLabel(group.category)
    : null;

  return (
    <article className="rounded-lg border border-border/60 bg-background/40">
      <div className="flex shrink-0 items-start gap-1">
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="flex min-w-0 flex-1 items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent/30"
          aria-expanded={expanded}
        >
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium text-muted-foreground">
              {received}
            </p>
            {group.subject && (
              <p className="mt-0.5 truncate text-xs font-medium" title={group.subject}>
                {group.subject}
              </p>
            )}
            <p className="mt-1 text-[10px] text-muted-foreground">
              {[
                category,
                group.documents.length === 1
                  ? "1 file"
                  : `${group.documents.length} files`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="mt-1 mr-1 flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/30 hover:text-foreground"
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse documents" : "Expand documents"}
        >
          <ChevronDown
            className={cn(
              "size-4 transition-transform duration-200",
              expanded && "rotate-180",
            )}
          />
        </button>
      </div>
      {expanded && (
        <div className="border-t border-border/50 px-1.5 py-1">
          {group.from_address && (
            <p className="truncate px-1 pb-1 text-[10px] text-muted-foreground">
              From <SensitiveText value={group.from_address} />
            </p>
          )}
          {group.documents.map((document) => {
            const linkedBl = findLinkedBlDocument(document, blDocuments);
            return (
              <DocumentFileRow
                key={document.attachment_id}
                document={document}
                linkedBl={linkedBl}
                selected={linkedBl != null && selectedBlId === linkedBl.id}
                onOpenBl={onOpenBl}
              />
            );
          })}
        </div>
      )}
    </article>
  );
}

/**
 * Right-rail card listing Graph-mail documents linked to a booking, grouped
 * by received date. Rendered as its own glass-panel card (like Opportunities).
 */
export function BookingDocumentsRailSection({
  sourceId,
  stack = false,
  stackAnimate = false,
  stackGrow = false,
}: {
  sourceId: string;
  stack?: boolean;
  stackAnimate?: boolean;
  stackGrow?: boolean;
}) {
  const {
    toggleBookingDocumentsSection,
    applyBookingDocumentsAvailability,
    selectedBookingBlDocumentId,
    toggleBookingBlDocument,
  } = useRightRail();
  const view = useRailStackPanel("documents");
  const { documents, loading, error, retry, loadedSourceId } =
    useBookingDocuments(sourceId);
  const { documents: blDocuments } = useBookingBlDocuments(sourceId);
  const documentCount = useMemo(
    () => deduplicateBookingDocumentsByFilename(documents).length,
    [documents],
  );

  useEffect(() => {
    if (loading || loadedSourceId !== sourceId) return;
    applyBookingDocumentsAvailability(documentCount > 0);
  }, [
    applyBookingDocumentsAvailability,
    documentCount,
    loadedSourceId,
    loading,
    sourceId,
  ]);

  return (
    <CollapsibleRailSection
      title="Documents"
      icon={FileText}
      badge={documentCount > 0 ? documentCount : undefined}
      expanded={view.expanded}
      accordion={view.accordion}
      fill={view.fill}
      flow={view.flow}
      scroll={stackGrow ? false : view.scroll}
      onToggle={toggleBookingDocumentsSection}
      className={railSectionStackClassName(view, {
        mobileStack: stack,
        stackAnimate,
        stackGrow,
      })}
      contentClassName={stack ? undefined : "pr-0.5"}
    >
      <div className={railStackSectionContentClassName(stackGrow, "pr-0.5")}>
        <BookingDocumentsCard
          documents={documents}
          blDocuments={blDocuments}
          selectedBlId={selectedBookingBlDocumentId}
          onOpenBl={toggleBookingBlDocument}
          loading={loading}
          error={error}
          onRetry={retry}
          stackGrow={stackGrow}
        />
      </div>
    </CollapsibleRailSection>
  );
}

export function BookingDocumentsCard({
  documents,
  blDocuments,
  selectedBlId,
  onOpenBl,
  loading,
  error,
  onRetry,
  stackGrow = false,
}: BookingDocumentsCardProps) {
  const groups = useMemo(() => {
    const deduped = deduplicateBookingDocumentsByFilename(documents);
    return groupBookingDocumentsByReceived(deduped);
  }, [documents]);

  const listBody = (
    <>
      {loading && documents.length === 0 && (
        <div className="flex items-center gap-2 py-1">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Loading documents…
          </span>
        </div>
      )}

      {error && documents.length === 0 && (
        <div className="flex flex-col gap-2 py-1">
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}

      {!loading && !error && groups.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No documents linked to this booking yet.
        </p>
      )}

      {groups.length > 0 && (
        <div className="flex flex-col gap-2">
          {groups.map((group) => (
            <DocumentGroupCard
              key={group.message_id}
              group={group}
              blDocuments={blDocuments}
              selectedBlId={selectedBlId}
              onOpenBl={onOpenBl}
            />
          ))}
        </div>
      )}
    </>
  );

  return (
    <div
      className={railStackInlineScrollRootClassName(
        stackGrow,
        "flex flex-col",
      )}
    >
      {stackGrow ? (
        <div
          className={railStackInlineScrollBodyClassName(
            true,
            "flex flex-col gap-2 pr-0.5",
          )}
        >
          {listBody}
        </div>
      ) : (
        listBody
      )}
    </div>
  );
}
